import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import { MonitoringImage } from '../../../cctv-monitoring/cctv-monitoring.models';
import { MapVehicle, STATUSES } from '../../cctv-monitoring-map.models';

@Component({
  selector: 'app-monitoring-map',
  templateUrl: './monitoring-map.component.html',
  styleUrls: ['./monitoring-map.component.css'],
})
export class MonitoringMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLElement>;
  @Input() vehicles: MapVehicle[] = [];
  @Input() images: MonitoringImage[] = [];
  @Input() category: 'cabin' | 'outside' = 'cabin';
  @Input() showThumbnails = true;
  @Input() clustersEnabled = true;
  @Input() hasLoaded = false;
  @Output() readonly vehicleSelected = new EventEmitter<MapVehicle>();

  private map?: L.Map;
  private markers?: L.LayerGroup;
  private resizeObserver?: ResizeObserver;
  private initialFitDone = false;
  private readonly initialCenter: L.LatLngExpression = [35.7, 51.4];
  private readonly initialZoom = 6;

  ngAfterViewInit(): void {
    const tileUrl = (window as any).strMapUrl as string | undefined;
    if (!tileUrl) return;
    this.map = L.map(this.mapContainer.nativeElement, { zoomControl: false })
      .setView(this.initialCenter, this.initialZoom);
    L.control.zoom({ position: 'bottomleft' }).addTo(this.map);
    L.tileLayer(tileUrl, { maxZoom: 19, maxNativeZoom: 14, attribution: 'Map data © RayanAndishehNasr' })
      .addTo(this.map);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize({ pan: false }));
      this.resizeObserver.observe(this.mapContainer.nativeElement);
    }
    this.map.on('zoomend', () => this.renderMarkers());
    this.renderMarkers();
    this.fitInitially();
  }

  ngOnChanges(): void {
    this.renderMarkers();
    this.fitInitially();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.map?.remove();
  }

  fitVehicles(vehicles: MapVehicle[] = this.vehicles): void {
    if (!this.map) return;
    if (vehicles.length) {
      const bounds = L.latLngBounds(vehicles.map(vehicle => [Number(vehicle.fLat), Number(vehicle.fLon)] as L.LatLngTuple));
      this.map.fitBounds(bounds, { padding: [55, 55], maxZoom: 14 });
    } else this.map.setView(this.initialCenter, this.initialZoom);
  }

  zoomToVehicle(vehicle: MapVehicle): void {
    this.map?.setView([Number(vehicle.fLat), Number(vehicle.fLon)], 17, { animate: true });
  }

  private fitInitially(): void {
    if (this.map && this.hasLoaded && !this.initialFitDone) {
      this.initialFitDone = true;
      this.fitVehicles();
    }
  }

  private vehicleId(vehicle: MapVehicle): string {
    return String(vehicle.strUnitID || vehicle.strUnitId || '').trim();
  }

  private renderMarkers(): void {
    if (!this.map) return;
    if (this.markers) this.map.removeLayer(this.markers);
    this.markers = L.layerGroup().addTo(this.map);
    for (const group of this.buildClusters()) {
      if (group.length > 1) {
        this.markers.addLayer(this.createClusterMarker(group));
        continue;
      }
      const vehicle = group[0];
      const image = this.images.find(item => item.unitId === this.vehicleId(vehicle) && item.category === this.category);
      const node = document.createElement('div');
      node.className = 'vehicle-marker';
      node.style.setProperty('--status', (STATUSES[Number(vehicle.tiDeviceStatus)] || STATUSES[0]).color);
      if (this.showThumbnails) {
        if (image) {
          const thumb = document.createElement('img');
          thumb.className = 'marker-thumb';
          thumb.src = image.thumbnailUrl;
          thumb.alt = '';
          let triedFullImage = image.thumbnailUrl === image.fullUrl;
          thumb.onerror = () => {
            if (!triedFullImage) { triedFullImage = true; thumb.src = image.fullUrl; }
            else thumb.replaceWith(this.emptyThumbnail());
          };
          node.appendChild(thumb);
        } else node.appendChild(this.emptyThumbnail());
      }
      const caption = document.createElement('span');
      caption.className = 'marker-caption';
      caption.textContent = vehicle.strFullName || this.vehicleId(vehicle);
      node.appendChild(caption);
      const dot = document.createElement('span');
      dot.className = 'marker-dot';
      const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      arrow.setAttribute('viewBox', '0 0 24 24');
      arrow.setAttribute('aria-hidden', 'true');
      const angle = Number(vehicle.siAngle);
      arrow.style.transform = `rotate(${Number.isFinite(angle) ? angle : 0}deg)`;
      const arrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      arrowPath.setAttribute('fill', 'currentColor');
      arrowPath.setAttribute('d', 'M12 2 3 21l9-5 9 5z');
      arrow.appendChild(arrowPath);
      dot.appendChild(arrow);
      node.appendChild(dot);
      const icon = L.divIcon({ className: 'vehicle-icon', html: node,
        iconSize: [110, this.showThumbnails ? 116 : 56], iconAnchor: [55, this.showThumbnails ? 108 : 48] });
      const marker = L.marker([Number(vehicle.fLat), Number(vehicle.fLon)], { icon, title: vehicle.strFullName || this.vehicleId(vehicle) });
      marker.on('click', () => this.vehicleSelected.emit(vehicle));
      this.markers.addLayer(marker);
    }
  }

  private buildClusters(): MapVehicle[][] {
    if (!this.clustersEnabled || !this.map) return this.vehicles.map(vehicle => [vehicle]);
    const grid = new Map<string, MapVehicle[]>();
    const cellSize = this.showThumbnails ? 122 : 65;
    for (const vehicle of this.vehicles) {
      const point = this.map.project([Number(vehicle.fLat), Number(vehicle.fLon)], this.map.getZoom());
      const key = `${Math.floor(point.x / cellSize)}:${Math.floor(point.y / cellSize)}`;
      const group = grid.get(key) || [];
      group.push(vehicle);
      grid.set(key, group);
    }
    return Array.from(grid.values());
  }

  private createClusterMarker(group: MapVehicle[]): L.Marker {
    const lat = group.reduce((sum, vehicle) => sum + Number(vehicle.fLat), 0) / group.length;
    const lon = group.reduce((sum, vehicle) => sum + Number(vehicle.fLon), 0) / group.length;
    const node = document.createElement('div');
    node.className = 'map-cluster';
    node.textContent = group.length.toLocaleString('fa-IR');
    const marker = L.marker([lat, lon], {
      icon: L.divIcon({ className: 'cluster-icon', html: node, iconSize: [56, 56], iconAnchor: [28, 28] }),
      title: `${group.length.toLocaleString('fa-IR')} خودرو در این محدوده`,
    });
    marker.on('click', () => {
      if (!this.map) return;
      const zoom = this.map.getZoom();
      if (zoom < 19) {
        const bounds = L.latLngBounds(group.map(vehicle => [Number(vehicle.fLat), Number(vehicle.fLon)] as L.LatLngTuple));
        if (bounds.getNorthEast().distanceTo(bounds.getSouthWest()) > 15) {
          this.map.fitBounds(bounds.pad(.4), { maxZoom: Math.min(19, zoom + 3) });
        } else this.map.setView([lat, lon], Math.min(19, zoom + 2));
        return;
      }
      const list = document.createElement('div');
      list.className = 'cluster-vehicles';
      list.setAttribute('dir', 'rtl');
      for (const vehicle of group) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = vehicle.strFullName || this.vehicleId(vehicle);
        button.addEventListener('click', () => {
          this.map?.closePopup();
          this.vehicleSelected.emit(vehicle);
        });
        list.appendChild(button);
      }
      marker.bindPopup(list, { maxHeight: 240 }).openPopup();
    });
    return marker;
  }

  private emptyThumbnail(): HTMLElement {
    const empty = document.createElement('span');
    empty.className = 'marker-thumb marker-empty';
    empty.textContent = 'بدون تصویر';
    return empty;
  }
}
